/*
 * Copyright (c) 2024. Bolmate B.V.
 */

import React, {useMemo} from 'react';
import Chart from "react-apexcharts";
import {ApexBarSerie, SearchVolumeData} from "../../util/interfaces";

interface Props {
    searchVolumes: Array<SearchVolumeData>;
    width?: number | string;
    height?: number | string;
}

function getSearchVolumeLineData(data: SearchVolumeData): [string, Array<string>, Array<number>] {
    const labels: Array<string> = [];
    const values: Array<number> = [];
    for (let ix = 0; ix < data.periods.length; ix++) {
        const period = data.periods[ix];
        labels.push(period.period);
        values.push(period.total);
    }
    return [data.search_term, labels, values];
}

export default function SearchVolumeLineChart(props: Props) {
    const {searchVolumes, width, height} = props;
    const [series, options] = useMemo(() => {
        let xLabels: Array<string> = [];
        const series: Array<ApexBarSerie> = [];
        for (const searchVolume of searchVolumes) {
            const [seriesName, labels, serieValues] = getSearchVolumeLineData(searchVolume);
            xLabels = xLabels.concat(labels);
            series.push({name: seriesName, data: serieValues});
        }
        const options = {
            chart: {
                toolbar: {show: false},
                zoom: {enabled: false},
            },
            xaxis: {
                categories: Array.from(new Set(xLabels)),
                labels: {
                    rotate: -45,
                    rotateAlways: true,
                }
            },
            yaxis: {min: 0},
            legend: {
                markers: {
                    customHTML: function() {
                        return '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="check" class="svg-inline--fa fa-check legend-check" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">' +
                            '<path fill="currentColor" d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>' +
                            '</svg>'
                    }
                }
            },
            stroke: {
                curve: 'smooth' as 'smooth',
            },
            dataLabels: {enabled: false},
        }
        return [series, options];
    }, [searchVolumes]);

    return (
        <div className='search-volume-bar-chart'>
            <Chart options={options} series={series} type='area' width={width} height={height}/>
        </div>
    )
}